import { PhoneOutline24Icon } from "@vygruppen/spor-icon-react";
import { useGetTrainStations } from "features/VehicleSidebar/VehicleList/AddVehicles/useInfoDataForVehicles";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { CallButton } from "shared/components/buttons/CallButton";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import { Badge, Box, Button, Flex, Text, tokens } from "shared/components/Spor";
import type { StopReference } from "shared/types/alternativeTransport";
import { VehicleType } from "shared/types/alternativeTransport";
import { toSentenceCase } from "shared/utils/alternativeTransport";
import styled from "styled-components";

const SummaryLayout = styled(Flex)`
  @container altTransportPage (width < 1696px) {
    flex-direction: column;
  }
`;

const SummaryField = styled(Flex)`
  @container altTransportPage (width < 1696px) {
    && {
      flex-direction: row;
      min-width: 0;
      align-items: center;
      gap: ${tokens.default.size.spacing[1.5]};
    }
  }
`;

const SummaryFieldTitle = styled(Text)`
  @container altTransportPage (width < 1696px) {
    min-width: 120px;

    flex-shrink: 0;
  }
`;

// Finds the train station corresponding to the given base stop reference.
const useBaseStopStation = (baseStopReference?: StopReference | null) => {
  const { data } = useGetTrainStations(
    Boolean(baseStopReference?.jbvId || baseStopReference?.nsrCode),
  );

  return useMemo(
    () =>
      data?.railStations.find((trainStation) =>
        trainStation.externalIds.some(
          (externalId) =>
            (externalId.sourceSystem === "TRAINPLAN" &&
              externalId.id === baseStopReference?.jbvId) ||
            (externalId.sourceSystem === "NSR" &&
              externalId.id === baseStopReference?.nsrCode),
        ),
      ),
    [data?.railStations, baseStopReference?.jbvId, baseStopReference?.nsrCode],
  );
};

const getVehicleText = (vehicleType: VehicleType) => {
  const { t } = useTranslation();
  switch (vehicleType) {
    case "BUS":
      return t("Buss");
    case "MINIBUS":
      return t("Minibuss");
    case "TAXI":
      return t("Taxi");
    case "MAXITAXI":
      return t("MaxiTaxi");
    case "UNKNOWN":
      return t("Ukjent kjøretøy");
  }
};

const Component = ({
  title,
  size = "md",
  child,
}: {
  title: string;
  size?: "sm" | "md" | "lg";

  child: string | React.ReactNode;
}) => {
  let minWidth = "90px";

  if (size === "md") {
    minWidth = "120px";
  } else if (size === "lg") {
    minWidth = "240px";
  }

  return (
    <SummaryField direction="column" gap="0.5" minWidth={minWidth}>
      <SummaryFieldTitle
        fontWeight="700"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        overflow="hidden"
      >
        {title}
      </SummaryFieldTitle>
      {typeof child === "string" ? <Text>{child}</Text> : child}
    </SummaryField>
  );
};

type VehicleSummaryCommonProps = {
  type: VehicleType;
  driverPhoneNumber: string | null;
  operatorName: string | null;
};

type VehicleSummaryProps = VehicleSummaryCommonProps &
  (
    | {
        variant: "operational";
        tripDetails?: string | null;
        stations?: string | null;
        trains?: string | null;
        lineNames?: string | null;
        startTime?: Date | null;
        endTime?: Date | null;
        availableFrom?: never;
        availableTo?: never;
        location?: never;
        reference?: never;
        baseStopReference?: never;
      }
    | {
        variant: "reserve-backup";
        availableFrom?: Date | null;
        availableTo?: Date | null;
        location?: string | null;
        reference?: string | null;
        baseStopReference?: StopReference | null;
        tripDetails?: never;
        stations?: never;
        trains?: never;
        lineNames?: never;
        startTime?: never;
        endTime?: never;
      }
    | {
        variant: "shift";
        startTime?: Date | null;
        endTime?: Date | null;
        tripDetails?: never;
        stations?: never;
        trains?: never;
        lineNames?: never;
        availableFrom?: never;
        availableTo?: never;
        location?: never;
        reference?: never;
        baseStopReference?: never;
      }
  );

export const VehicleSummary = ({
  variant: _variant,
  type,
  stations,
  trains,
  tripDetails,
  driverPhoneNumber,
  lineNames,
  operatorName,
  startTime,
  endTime,
  availableFrom,
  availableTo,
  location,
  reference,
  baseStopReference,
}: VehicleSummaryProps) => {
  const typeText = getVehicleText(type);
  const station = useBaseStopStation(baseStopReference);

  const phoneNumberWithDigitsOnly = driverPhoneNumber
    ? driverPhoneNumber.replace("+", "00")
    : null;

  const overflowCropFixPadding = "1px";

  return (
    <SummaryLayout gap="2">
      <Component
        title={operatorName ?? "[Leverandør]"}
        child={
          <Box pl={overflowCropFixPadding} pb={overflowCropFixPadding}>
            {phoneNumberWithDigitsOnly ? (
              <CallButton phoneNumber={phoneNumberWithDigitsOnly} size="xs" />
            ) : (
              <Button
                leftIcon={<PhoneOutline24Icon />}
                variant="tertiary"
                disabled={!driverPhoneNumber}
                size="xs"
              >
                Ikke registrert
              </Button>
            )}
          </Box>
        }
        size="lg"
      />
      <Component title="Kjøretøy" size="sm" child={typeText} />
      {startTime && endTime && (
        <Component
          title="Tidsperiode"
          child={
            <TimeInterval
              variant="sm"
              startTime={startTime}
              endTime={endTime}
            />
          }
          size="lg"
        />
      )}

      {availableFrom && availableTo && (
        <Component
          title="Tilgjengelig"
          child={
            <TimeInterval
              variant="sm"
              startTime={availableFrom}
              endTime={availableTo}
            />
          }
          size="lg"
        />
      )}

      {trains && lineNames && tripDetails && (
        <Component title={`${trains} ${lineNames}`} child={tripDetails} />
      )}
      {stations && <Component title="Strekning" child={stations} size="lg" />}

      {location && <Component title="Lokasjon" child={location} size="lg" />}
      {station && <Component title="Stasjon" child={station.name} size="lg" />}
      {reference && <Component title="Referanse" child={reference} size="sm" />}
    </SummaryLayout>
  );
};

export const VehicleTitle = ({
  externalSystem,
  isAvailable = true, // Default to true if not provided, to not show "I bruk" badge
  workShiftId,
  type,
}: {
  externalSystem?: string | null;
  isAvailable?: boolean;
  workShiftId: string | null;
  type: "BACKUP" | "RESERVE" | "SHIFT";
}) => {
  const location = useLocation();

  let title = "Beredskap";

  if (type === "RESERVE") {
    title = "Reserve";
  } else if (type === "SHIFT") {
    title = "Skift";
  }

  return (
    <Flex gap="1" alignItems="center">
      <Text variant="ml" fontWeight="700">
        {location.state?.vehicleTitle ?? `${title} ${workShiftId}`}
      </Text>
      {externalSystem && (
        <>
          <Text variant="ml" fontWeight="400">
            |
          </Text>

          <Text variant="ml" fontWeight="400">
            {toSentenceCase(externalSystem)}
          </Text>
        </>
      )}

      {!isAvailable && (
        <Badge ml="0.5" colorPalette="blue">
          I bruk
        </Badge>
      )}
    </Flex>
  );
};
